/* ==========================================================================
   Accessibility widget

   Preferences live in localStorage and are re-applied on every page load by
   the inline bootstrap in sections/accessibility-widget.liquid, which runs
   before first paint so a visitor who chose high contrast never sees a flash
   of the default palette while this file is still downloading.
   ========================================================================== */

(() => {
  const STORAGE_KEY = 'a11y-prefs';

  const STEPS = {
    fontScale: { min: 1, max: 1.6, step: 0.1, unit: '%', base: 1 },
    lineHeight: { min: 1, max: 2, step: 0.25, unit: '%', base: 1 },
    spacing: { min: 0, max: 3, step: 1, unit: '', base: 0 },
    zoom: { min: 1, max: 2, step: 0.25, unit: '%', base: 1 },
  };

  const FLAGS = [
    'readableFont',
    'highlightLinks',
    'highlightTitles',
    'focusRing',
    'keyboardNav',
    'imageDescriptions',
    'bigCursor',
    'bigCursorLight',
    'noMotion',
    'monochrome',
    'invert',
  ];

  const FLAG_CLASS = {
    readableFont: 'a11y-readable-font',
    highlightLinks: 'a11y-highlight-links',
    highlightTitles: 'a11y-highlight-titles',
    focusRing: 'a11y-focus-ring',
    keyboardNav: 'a11y-keyboard-nav',
    imageDescriptions: 'a11y-image-descriptions',
    bigCursor: 'a11y-big-cursor',
    bigCursorLight: 'a11y-big-cursor-light',
    noMotion: 'a11y-no-motion',
    monochrome: 'a11y-monochrome',
    invert: 'a11y-invert',
  };

  // Turning one on turns the other off: two cursors cannot both be the pointer.
  const MUTUALLY_EXCLUSIVE = [['bigCursor', 'bigCursorLight']];

  // Shortcut key -> what it cycles through, active only in keyboard-nav mode.
  const JUMP_TARGETS = {
    KeyH: 'h1, h2, h3, h4, h5, h6',
    KeyL: 'a[href]',
    KeyB: 'button, [role="button"], input[type="submit"]',
    KeyG: 'img',
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  const defaults = () => ({
    fontScale: 1,
    lineHeight: 1,
    spacing: 0,
    zoom: 1,
    contrast: null,
    flags: FLAGS.reduce((acc, key) => ({ ...acc, [key]: false }), {}),
  });

  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaults();
      const saved = JSON.parse(raw);
      return { ...defaults(), ...saved, flags: { ...defaults().flags, ...(saved.flags || {}) } };
    } catch {
      return defaults();
    }
  }

  function write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* Private browsing and full quotas both throw here. The preferences
         still apply to the current page; only persistence is lost. */
    }
  }

  function apply(state) {
    const root = document.documentElement;

    root.style.setProperty('--a11y-font-scale', String(state.fontScale));
    root.style.setProperty('--a11y-line-height', String(state.lineHeight));
    root.style.setProperty('--a11y-letter-spacing', `${state.spacing * 0.05}em`);
    root.style.setProperty('--a11y-word-spacing', `${state.spacing * 0.16}em`);
    root.style.setProperty('--a11y-zoom', String(state.zoom));

    root.classList.toggle('a11y-active', state.fontScale !== 1);
    root.classList.toggle('a11y-line-height', state.lineHeight !== 1);
    root.classList.toggle('a11y-spacing', state.spacing !== 0);
    root.classList.toggle('a11y-zoom', state.zoom !== 1);
    root.classList.toggle('a11y-contrast-dark', state.contrast === 'dark');
    root.classList.toggle('a11y-contrast-light', state.contrast === 'light');

    FLAGS.forEach((key) => root.classList.toggle(FLAG_CLASS[key], !!state.flags[key]));
  }

  /* ------------------------------------------------- image descriptions -- */

  /* Alt text is drawn as a caption laid over each image. The caption is
     absolutely positioned, which needs a positioned ancestor, so a static
     parent is temporarily promoted to relative and restored on the way out —
     inserting a sibling into the flow instead would disturb the grid and flex
     layouts these images sit in. */
  function showImageDescriptions(on) {
    if (!on) {
      document.querySelectorAll('.a11y-alt').forEach((el) => el.remove());
      document.querySelectorAll('[data-a11y-captioned]').forEach((el) => {
        delete el.dataset.a11yCaptioned;
      });
      document.querySelectorAll('[data-a11y-was-static]').forEach((el) => {
        el.style.position = el.dataset.a11yWasStatic === 'inline' ? '' : el.dataset.a11yWasStatic;
        delete el.dataset.a11yWasStatic;
      });
      return;
    }

    document.querySelectorAll('img').forEach((img) => {
      if (img.closest('.a11y-root') || img.dataset.a11yCaptioned) return;
      const parent = img.parentElement;
      if (!parent) return;
      if (img.offsetWidth < 60 || img.offsetHeight < 60) return;

      if (getComputedStyle(parent).position === 'static') {
        parent.dataset.a11yWasStatic = parent.style.position || 'inline';
        parent.style.position = 'relative';
      }

      const alt = (img.getAttribute('alt') || '').trim();
      const caption = document.createElement('span');
      caption.className = alt ? 'a11y-alt' : 'a11y-alt a11y-alt--missing';
      caption.textContent = alt || 'תמונה ללא תיאור חלופי';

      // Pinned to this image's own box rather than to the container's bottom
      // edge: several images commonly share one parent, and anchoring to the
      // parent would pile every caption in the same corner.
      caption.style.left = img.offsetLeft + 'px';
      caption.style.top = img.offsetTop + img.offsetHeight + 'px';
      caption.style.width = img.offsetWidth + 'px';

      img.dataset.a11yCaptioned = '';
      parent.appendChild(caption);
    });
  }

  class AccessibilityWidget {
    constructor(root) {
      this.root = root;
      this.toggle = root.querySelector('[data-a11y-toggle-panel]');
      this.panel = root.querySelector('[data-a11y-panel]');
      this.state = read();
      this.lastFocus = null;

      if (!this.toggle || !this.panel) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.toggle.addEventListener('click', () => this.togglePanel());
      root.querySelectorAll('[data-a11y-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      root.querySelectorAll('[data-a11y-flag]').forEach((el) =>
        el.addEventListener('click', () => this.toggleFlag(el.dataset.a11yFlag))
      );

      root.querySelectorAll('[data-a11y-contrast]').forEach((el) =>
        el.addEventListener('click', () => this.setContrast(el.dataset.a11yContrast))
      );

      root.querySelectorAll('[data-a11y-step]').forEach((el) =>
        el.addEventListener('click', () =>
          this.step(el.dataset.a11yStep, Number(el.dataset.a11yDirection))
        )
      );

      const reset = root.querySelector('[data-a11y-reset]');
      if (reset) reset.addEventListener('click', () => this.reset());

      document.addEventListener('keydown', (event) => {
        // Alt+Shift+A opens the panel from anywhere on the page.
        if (event.altKey && event.shiftKey && event.code === 'KeyA') {
          event.preventDefault();
          this.togglePanel();
          return;
        }

        if (!this.state.flags.keyboardNav) return;

        // Never swallow a keystroke meant for a form field or the panel.
        const el = document.activeElement;
        if (
          el &&
          (el.isContentEditable ||
            el.matches('input, textarea, select') ||
            this.panel.contains(el))
        ) {
          return;
        }
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        const selector = JUMP_TARGETS[event.code];
        if (!selector) return;
        event.preventDefault();
        this.jump(selector, event.shiftKey);
      });

      // The tint layers blend with whatever is painted beneath them in their
      // own stacking context, so they have to sit at the end of <body> rather
      // than inside this section's wrapper.
      document
        .querySelectorAll('[data-a11y-canvas]')
        .forEach((layer) => document.body.appendChild(layer));

      apply(this.state);
      if (this.state.flags.imageDescriptions) showImageDescriptions(true);
      this.sync();
    }

    /* ------------------------------------------------------------ panel -- */

    get isOpen() {
      return !this.panel.hasAttribute('hidden');
    }

    togglePanel() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.lastFocus = document.activeElement;
      this.panel.removeAttribute('hidden');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      const first = this.panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.panel.setAttribute('hidden', '');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);

      // Return focus to whatever opened the panel. When the panel was opened
      // without focusing anything first — a script call, or a mouse click in a
      // browser that does not focus buttons on click — document.activeElement
      // was <body>, which is not somewhere a keyboard user can resume from, so
      // fall back to the toggle.
      const resumeAt =
        this.lastFocus &&
        this.lastFocus !== document.body &&
        document.contains(this.lastFocus)
          ? this.lastFocus
          : this.toggle;

      resumeAt.focus();
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      // Keep keyboard focus inside the panel while it is open, so a visitor
      // tabbing through the controls never lands on the page behind it.
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ----------------------------------------------------------- state -- */

    commit() {
      apply(this.state);
      write(this.state);
      this.sync();
    }

    toggleFlag(key) {
      if (!(key in this.state.flags)) return;
      const next = !this.state.flags[key];
      this.state.flags[key] = next;

      if (next) {
        MUTUALLY_EXCLUSIVE.forEach((group) => {
          if (!group.includes(key)) return;
          group.filter((other) => other !== key).forEach((other) => {
            this.state.flags[other] = false;
          });
        });
      }

      if (key === 'imageDescriptions') showImageDescriptions(next);
      this.commit();
    }

    setContrast(mode) {
      // The two contrast modes replace each other; picking the active one again
      // turns it off.
      this.state.contrast = this.state.contrast === mode ? null : mode;
      this.commit();
    }

    step(key, direction) {
      const spec = STEPS[key];
      if (!spec) return;
      const next = Number((this.state[key] + spec.step * direction).toFixed(2));
      this.state[key] = Math.min(spec.max, Math.max(spec.min, next));
      this.commit();
    }

    reset() {
      this.state = defaults();
      showImageDescriptions(false);
      this.clearLanded();
      this.commit();
      this.announce();
    }

    /* --------------------------------------------------- keyboard jumps -- */

    clearLanded() {
      document
        .querySelectorAll('[data-a11y-landed]')
        .forEach((el) => el.removeAttribute('data-a11y-landed'));
    }

    jump(selector, backwards) {
      const items = [...document.querySelectorAll(selector)].filter((el) => {
        if (el.closest('.a11y-root')) return false;
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      });
      if (!items.length) return;

      const current = document.querySelector('[data-a11y-landed]');
      const at = current ? items.indexOf(current) : -1;
      const next = backwards
        ? items[(at <= 0 ? items.length : at) - 1]
        : items[(at + 1) % items.length];

      this.clearLanded();
      next.setAttribute('data-a11y-landed', '');

      // Headings and images are not focusable, so give them a temporary
      // programmatic focus target rather than only scrolling to them: screen
      // readers announce what receives focus.
      if (!next.hasAttribute('tabindex') && !next.matches('a[href], button, input')) {
        next.setAttribute('tabindex', '-1');
      }
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'center', behavior: 'auto' });
    }

    announce() {
      const live = this.root.querySelector('[data-a11y-live]');
      if (!live) return;
      live.textContent = live.dataset.a11yResetMessage || '';
      window.setTimeout(() => {
        live.textContent = '';
      }, 1500);
    }

    /* ---------------------------------------------------------- display -- */

    sync() {
      this.root.querySelectorAll('[data-a11y-flag]').forEach((el) => {
        el.setAttribute('aria-pressed', String(!!this.state.flags[el.dataset.a11yFlag]));
      });

      this.root.querySelectorAll('[data-a11y-contrast]').forEach((el) => {
        el.setAttribute('aria-pressed', String(this.state.contrast === el.dataset.a11yContrast));
      });

      this.root.querySelectorAll('[data-a11y-value]').forEach((el) => {
        const key = el.dataset.a11yValue;
        const spec = STEPS[key];
        if (!spec) return;
        el.textContent =
          spec.unit === '%'
            ? `${Math.round(this.state[key] * 100)}%`
            : `${this.state[key]}`;
      });

      this.root.querySelectorAll('[data-a11y-step]').forEach((el) => {
        const spec = STEPS[el.dataset.a11yStep];
        if (!spec) return;
        const value = this.state[el.dataset.a11yStep];
        const dir = Number(el.dataset.a11yDirection);
        el.disabled = dir > 0 ? value >= spec.max : value <= spec.min;
      });
    }
  }

  // Product pages slide a buy bar up from the bottom of the screen, and the
  // button must not end up behind it.
  //
  // The theme itself never works this out. Shrine's hosted script does, and
  // writes the height it arrives at onto the floating buttons it ships — the
  // scroll-to-top arrow and the music player — but only onto those, and only
  // onto the ones already on the page when it loads. So this takes the very
  // number it gave them: the button then lifts by the same amount, at the same
  // moment, as the arrow beside it. Where those buttons are switched off, or
  // that script is not running, the bar is measured here instead, and with no
  // bar at all the value is zero and nothing moves.
  function followStickyBar(root) {
    const twin = document.querySelector('.scroll-to-top-btn, .music-player');
    const bar = document.querySelector('sticky-atc, .sticky-atc');
    if (!twin && !bar) return;

    let frame = null;
    const measure = () => {
      frame = null;
      let offset = twin && getComputedStyle(twin).getPropertyValue('--sticky-atc-offset').trim();
      if (!offset && bar) {
        const box = bar.getBoundingClientRect();
        const covered = Math.max(0, Math.min(box.height, window.innerHeight - box.top));
        offset = `${Math.round(covered)}px`;
      }
      root.style.setProperty('--a11y-sticky-offset', offset || '0px');
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };
    // The bar slides in and out, so it is followed through the movement rather
    // than measured once at the start of it.
    const follow = () => {
      const until = Date.now() + 400;
      const step = () => {
        measure();
        if (Date.now() < until) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // the moment Shrine writes the height onto the arrow, this button takes it
    if (twin) {
      new MutationObserver(schedule).observe(twin, { attributes: true, attributeFilter: ['style'] });
    }
    if (bar) {
      bar.addEventListener('transitionend', schedule);
      new MutationObserver(follow).observe(bar, {
        attributes: true,
        attributeFilter: ['data-after-scroll', 'class', 'style'],
      });
      if (window.ResizeObserver) new ResizeObserver(schedule).observe(bar);
    }
  }

  function init() {
    document.querySelectorAll('[data-a11y-root]').forEach((root) => {
      new AccessibilityWidget(root);
      followStickyBar(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
